import React, { useState } from 'react';

const modules = [
  {
    icon: '🎫',
    name: 'Service Request',
    desc: 'End-to-end service request lifecycle management. Users submit, track, and close requests through a self-service portal. Auto-route to the right team instantly.',
    tags: ['Self-service portal', 'Auto-routing', 'SLA tracking'],
  },
  {
    icon: '⚠️',
    name: 'Incident Management',
    desc: 'Detect, log, classify, and resolve incidents fast. Built-in priority escalation, SLA breach alerts, and automated notifications keep teams on top of every outage.',
    tags: ['Priority escalation', 'Breach alerts', 'Auto-notify'],
  },
  {
    icon: '🔄',
    name: 'Problem Management',
    desc: 'Identify root causes behind recurring incidents and eliminate them permanently. Link related incidents to problems, track investigations, and close the loop.',
    tags: ['Root cause analysis', 'Incident linking', 'Known errors DB'],
  },
  {
    icon: '📋',
    name: 'Change Management',
    desc: 'Plan, review, approve, and implement IT changes with full audit trails. CAB workflows, risk assessment, and rollback planning built right in.',
    tags: ['CAB workflows', 'Risk assessment', 'Rollback planning'],
  },
  {
    icon: '📚',
    name: 'Knowledge Management',
    desc: 'Build a searchable knowledge base so your team and users can self-resolve common issues. Rich-text articles, categories, and version history included.',
    tags: ['Searchable KB', 'Self-resolution', 'Version history'],
  },
  {
    icon: '🐛',
    name: 'Defect Management',
    desc: 'Track and manage software defects from discovery to resolution. Link defects to incidents and changes for end-to-end visibility across your IT landscape.',
    tags: ['Bug tracking', 'Incident linking', 'Dev integration'],
  },
  {
    icon: '✨',
    name: 'Enhancement',
    desc: 'Manage feature requests and improvement ideas submitted by users or internal teams. Prioritise, assign, and track enhancements alongside your service delivery.',
    tags: ['Feature requests', 'Prioritisation', 'Stakeholder visibility'],
  },
  {
    icon: '⚙️',
    name: 'Configuration & CMDB',
    desc: 'Maintain an accurate inventory of your IT assets and configuration items. Map dependencies, track changes, and reduce risk with a live CMDB.',
    tags: ['Asset inventory', 'Dependency mapping', 'Live CMDB'],
  },
];

const card = (active) => ({
  background: active ? 'linear-gradient(135deg, rgba(45,106,79,0.2), rgba(20,28,53,0.8))' : '#141c35',
  border: `1px solid ${active ? 'rgba(82,183,136,0.4)' : 'rgba(255,255,255,0.07)'}`,
  borderRadius: '16px', padding: '28px 24px',
  cursor: 'pointer', transition: 'all 0.25s',
  transform: active ? 'translateY(-4px)' : 'none',
  boxShadow: active ? '0 20px 48px rgba(45,106,79,0.2)' : 'none',
});

const tag = { background: 'rgba(255,255,255,0.06)', borderRadius: '100px', padding: '3px 12px', fontSize: '11px', color: '#a8b4cc', fontWeight: 500 };

export default function Modules() {
  const [active, setActive] = useState(0);

  return (
    <section id="modules" style={{ padding: '100px 0', background: 'rgba(255,255,255,0.01)' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 32px' }}>
        <div style={{ textAlign: 'center', marginBottom: '64px' }}>
          <div style={{ display: 'inline-block', background: 'rgba(45,106,79,0.12)', border: '1px solid rgba(82,183,136,0.2)', borderRadius: '100px', padding: '5px 16px', fontSize: '12px', color: '#52b788', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '20px' }}>
            Complete ITSM Suite
          </div>
          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(32px,4vw,52px)', fontWeight: 800, letterSpacing: '-1.5px', marginBottom: '16px' }}>
            Every module your IT team needs
          </h2>
          <p style={{ fontSize: '17px', color: '#a8b4cc', maxWidth: '560px', margin: '0 auto' }}>
            From day-one service requests to complex root-cause investigations — ATLAS ITSM covers the full IT service delivery lifecycle.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }} className="modules-grid">
          {modules.map((m, i) => (
            <div key={m.name} style={card(active === i)} onClick={() => setActive(i)}
              onMouseEnter={e => { if (active !== i) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
              onMouseLeave={e => { if (active !== i) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; }}>
              <div style={{ fontSize: '28px', marginBottom: '14px' }}>{m.icon}</div>
              <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '16px', fontWeight: 700, color: '#fff', marginBottom: '10px' }}>{m.name}</h3>
              <p style={{ fontSize: '13px', color: '#a8b4cc', lineHeight: 1.6, marginBottom: '16px' }}>{m.desc}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {m.tags.map(t => <span key={t} style={tag}>{t}</span>)}
              </div>
            </div>
          ))}
        </div>
      </div>
      <style>{`@media(max-width:1024px){ .modules-grid{ grid-template-columns:repeat(2,1fr) !important; } } @media(max-width:600px){ .modules-grid{ grid-template-columns:1fr !important; } }`}</style>
    </section>
  );
}
